import { css } from "@emotion/css";
import { XmarkIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import React, { type ReactElement } from "react";
import type { ButtonBasicProps, ButtonLinkProps } from "../Button";
import { Button } from "../Button";
import { Divider } from "../Divider";
import { ErrorPanel } from "../Page";
import type { ErrorInfo } from "../Page";
import { DialogBase } from "./DialogBase";
type LimitedButtonProps = Pick<ButtonBasicProps, "label" | "onClick" | "disabled"> | Pick<ButtonLinkProps, "label" | "onClick" | "href" | "external">;
export type OnboardingDialogProps = {
    title: string;
    open: boolean;
    onClose: () => void;
    primaryButtonProps: LimitedButtonProps;
    secondaryButtonProps?: LimitedButtonProps;
    children: React.ReactNode;
    sidePanelImage?: ReactElement;
    errors?: ErrorInfo[];
};
export function OnboardingDialog({ children, open, onClose, title, primaryButtonProps, secondaryButtonProps, sidePanelImage, errors }: OnboardingDialogProps) {
    return (<DialogBase open={open} onClose={onClose} accessibleName={title} autoFocusFirstElement={false}>
            <div className={styles.container}>
                <section className={styles.mainPanel}>
                    <h2 className={styles.title}>{title}</h2>
                    <Divider />
                    <OnboardingDialogContent>
                        {errors?.map((err, index) => (<ErrorPanel error={err} key={index}/>))}
                        {children}
                    </OnboardingDialogContent>
                    <div className={styles.actions}>
                        <Button importance="loud" {...primaryButtonProps}/>
                        {secondaryButtonProps && <Button importance="quiet" {...secondaryButtonProps}/>}
                    </div>
                </section>

                <aside className={styles.sidePanel}>
                    <div className={styles.closeButton}>
                        <Button importance="ghost" accessibleName={"Close onboarding dialog"} onClick={onClose} disabled={false} size="medium" showExpandCollapseIcon={false} icon={<XmarkIcon size={20}/>}/>
                    </div>
                    {sidePanelImage && <div className={styles.sidePanelImage}>{sidePanelImage}</div>}
                </aside>
            </div>
        </DialogBase>);
}
interface OnboardingDialogContentProps {
    children: React.ReactNode;
}
function OnboardingDialogContent({ children }: OnboardingDialogContentProps) {
    return <div className={onboardingDialogContentStyles}>{children}</div>;
}
const onboardingDialogContentStyles = css({
    display: "flex",
    flexDirection: "column",
    padding: space[24],
    alignItems: "stretch",
    gap: space[24],
    alignSelf: "stretch",
    font: text.body.regular.large,
    "& p": {
        margin: 0,
    },
});
const breakpoints = {
    laptop: "1024px",
    tablet: "769px",
    mobile: "375px",
};
const styles = {
    container: css({
        position: "relative", // required to restrain the close button within the dialog
        display: "flex",
        flexDirection: "column-reverse",
        [`@media (min-width: ${breakpoints.laptop})`]: {
            flexDirection: "row",
        },
    }),
    title: css({
        font: text.heading.medium,
        padding: space[24],
        margin: 0,
    }),
    closeButton: css({
        position: "absolute",
        top: space[16],
        right: space[16],
        zIndex: 1,
    }),
    mainPanel: css({
        display: "flex",
        flexDirection: "column",
        color: themeTokens.color.text.primary,
        [`@media (min-width: ${breakpoints.laptop})`]: {
            width: "600px",
        },
    }),
    actions: css({
        justifyContent: "flex-end",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        gap: space[8],
        padding: `${space[8]} ${space[24]} ${space[24]} ${space[24]}`,
        "> button, > a": {
            width: "auto",
        },
    }),
    sidePanel: css({
        background: themeTokens.color.dialog.background.secondary.upsell,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: `${space[40]} ${space[32]}`,
        borderTopRightRadius: borderRadius.large,
        borderTopLeftRadius: borderRadius.large,
        [`@media (min-width: ${breakpoints.laptop})`]: {
            width: "400px",
            borderBottomRightRadius: borderRadius.large,
            borderTopLeftRadius: "initial",
        },
    }),
    sidePanelImage: css({
        width: "150px",
        height: "150px",
        [`@media (min-width: ${breakpoints.tablet})`]: {
            width: "248px",
            height: "248px",
        },
        [`@media (min-width: ${breakpoints.laptop})`]: {
            width: "304px",
            height: "304px",
        },
    }),
};
